import React, { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const InstallPWA: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if running as installed PWA (multiple detection methods)
    const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches;
    const isIOSStandalone = (window.navigator as any).standalone === true;
    const isInstalled = isStandaloneMode || isIOSStandalone;
    
    setIsStandalone(isInstalled);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(iOS);

    // Check if mobile device (not desktop)
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    // DEVELOPMENT MODE: Always show banner (ignore dismissal) for testing
    // TODO: Restore dismissal logic for production
    // const dismissedUntil = localStorage.getItem('pwa-install-dismissed-until');
    // const permanentlyDismissed = localStorage.getItem('pwa-install-dismissed');
    // const isDismissed = permanentlyDismissed === 'true' || 
    //   (dismissedUntil && Date.now() < parseInt(dismissedUntil));
    
    // Show on mobile devices if NOT installed (always show for testing)
    if (!isInstalled && isMobileDevice) {
      // For Android/Desktop - listen for beforeinstallprompt
      const handler = (e: Event) => {
        e.preventDefault();
        setDeferredPrompt(e as BeforeInstallPromptEvent);
        setShowInstallBanner(true);
      };

      window.addEventListener('beforeinstallprompt', handler);

      // For iOS - show custom banner after 3 seconds
      if (iOS) {
        setTimeout(() => {
          setShowInstallBanner(true);
        }, 3000);
      }

      return () => {
        window.removeEventListener('beforeinstallprompt', handler);
      };
    }
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Android/Desktop installation
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setShowInstallBanner(false);
        localStorage.setItem('pwa-install-dismissed', 'true');
      } else {
        // User cancelled - dismiss for 3 days only
        setShowInstallBanner(false);
        const threeDaysFromNow = Date.now() + (3 * 24 * 60 * 60 * 1000);
        localStorage.setItem('pwa-install-dismissed-until', threeDaysFromNow.toString());
      }
      
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowInstallBanner(false);
    // DEVELOPMENT MODE: Don't save dismissal (banner will reappear on page reload)
    // TODO: Restore for production
    // const threeDaysFromNow = Date.now() + (3 * 24 * 60 * 60 * 1000);
    // localStorage.setItem('pwa-install-dismissed-until', threeDaysFromNow.toString());
  };

  if (isStandalone || !showInstallBanner) {
    return null;
  }

  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: '#2563eb',
      color: '#fff',
      padding: '16px 20px',
      boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.15)',
      zIndex: 10000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
      flexWrap: 'wrap'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: '200px' }}>
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 16 16">
          <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm.5 4.5a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3a.5.5 0 0 1 .5-.5z"/>
        </svg>
        <div>
          <div style={{ fontWeight: '600', fontSize: '16px', marginBottom: '4px' }}>
            Install Price Tracker
          </div>
          {isIOS ? (
            <div style={{ fontSize: '13px', opacity: 0.95 }}>
              Tap <span style={{ 
                display: 'inline-flex', 
                alignItems: 'center',
                padding: '2px 6px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: '4px',
                margin: '0 4px'
              }}>
                <svg width="14" height="14" fill="currentColor" viewBox="0 0 16 16" style={{ marginRight: '4px' }}>
                  <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
                  <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
                </svg>
                Share
              </span> then "Add to Home Screen"
            </div>
          ) : (
            <div style={{ fontSize: '13px', opacity: 0.95 }}>
              Get quick access and work offline
            </div>
          )}
        </div>
      </div>
      
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        {!isIOS && (
          <button
            onClick={handleInstallClick}
            style={{
              backgroundColor: '#fff',
              color: '#2563eb',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 20px',
              fontWeight: '600',
              fontSize: '14px',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            Install
          </button>
        )}
        <button
          onClick={handleDismiss}
          style={{
            backgroundColor: 'transparent',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.5)',
            borderRadius: '6px',
            padding: '10px 16px',
            fontSize: '14px',
            cursor: 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          {isIOS ? 'Got it' : 'Dismiss'}
        </button>
      </div>
    </div>
  );
};

export default InstallPWA;
